export const meta = {
  title: 'Psyop',
  summary:
    'How an award-winning studio repaired imperfect motion capture without reshooting the performance.',
  category: 'case-study',
  date: '2026-02-07',
  hero: {
    type: 'image',
    cdnKey: 'blog_psyop1.jpg',
  },
  slug: 'psyop',
};

## An experienced studio encounters a familiar problem

[Psyop](https://www.psyop.com/) is an artist-founded, globally renowned creative studio known for award-winning work across film, animation, and mixed media. With deep experience in high-end production pipelines, their teams are accustomed to delivering technically precise, visually ambitious work. While producing a commercial featuring two conversing 3D characters, they encountered a familiar reality of motion capture: strong performances paired with motion data that wasn’t fully production-ready.

With a tight campaign timeline and no margin for reshoots, Psyop wanted a way to fix what was broken—without disrupting what already worked.

<ArticleVideo cdnKey="psyop1.webm" />

## The challenge: “Normal” mocap issues become schedule risks

Even in well-run mocap sessions, technical inconsistencies are part of the process. Calibration drift, tracking noise, and joint artifacts appear regularly—and resolving them often means revisiting earlier stages of production.

After reviewing their capture, Psyop identified several familiar issues:

- Over-rotated arm and wrist motion in select takes
- Floating or unstable body positioning
- Calibration inconsistencies across the session

In a standard workflow, correcting these problems would involve rebooking stage time, coordinating talent and crew, and reprocessing capture data. That process is proven, but slow.

With delivery deadlines approaching, Psyop wanted to explore Cartwheel as a faster way to stabilize the motion without compromising performance quality.

## Cartwheel's solution - A hybrid workflow that fixes only what’s broken

<ArticleVideo cdnKey="psyop2.webm" />

Rather than replacing their existing pipeline, Psyop and Cartwheel collaborated on a hybrid approach that combined traditional mocap with AI-based motion tools.

1. **Processed original footage** - Video from the original mocap shoot was run through Cartwheel’s Comic3 model to extract clean body motion.
2. **Reconstructed body motion** - Comic3 captured the actors' arm and body performances directly from video reference, correcting instability and rotation issues.
3. **Preserved high-fidelity details** - Head and hand details from the original mocap—where precision mattered most—were retained and spliced back into the final animation.

This allowed Psyop to correct problematic motion data without reshoots, rebooking, or starting over.

<MediaGrid
  items={[
    { type: 'video', cdnKey: 'psyop3.webm' },
    { type: 'video', cdnKey: 'psyop4.webm' },
  ]}
/>

## The impact: fewer reshoots, fewer delays, same creative intent

- **Streamlined iteration**: Eliminated the need for reshoot scheduling and stage rebooking
- **Schedule Protection**: Kept production on track to deliver final commercial spots on schedule
- **Performance quality**: The final characters preserved the intent and nuance of the original performances.

The collaboration showcased AI motion capture's versatility - not as a replacement for traditional pipelines, but as a **complementary and additional tool** that gives studios more options when addressing the everyday technical realities of mocap production.

We're grateful to the team at Psyop for the opportunity to collaborate on this project and explore new workflows together.

Learn more about Psyop:

- Website: [psyop.com](https://www.psyop.com/)
- LinkedIn: [linkedin.com/company/psyop](https://www.linkedin.com/company/psyop/)
- Instagram: [@psyop](https://www.instagram.com/psyop/)
